<script lang="ts">
	import { goto, pushState } from '$app/navigation';
	import { page } from '$app/stores';
	import type { CompleteVariant, RequiredProduct, SDSVariant } from '$lib/types';
	import { deduplicate } from '$lib/utils';
	import { mapToNode } from '$lib/utils/product';
	import { parseImageUrls } from '$lib/utils/image';
	import { classes } from '@thearc-hq/ui-kit/utils';
	import ButtonPrimary from '../Button/ButtonPrimary.svelte';
	import { getTranslate, T } from '@tolgee/svelte';
	import { browser } from '$app/environment';
	import { cart } from '$lib/store/cart.store';
	import ProductAddedToCartDrawer from '../Drawer/ProductAddedToCartDrawer.svelte';
	import { getGtmPayloadProductAddedToCart } from '$lib/gtm/productDetails';
	import { gtmDispatch } from '$lib/gtm/gtmDispatch';
	import { resolveRoute } from '$app/paths';
	import ProductShareButton from '$lib/components/Storyblok/ProductShareButton.svelte';
	import { outOfStockDrawerExpanded } from '$lib/store/outOfStock.store';
	import { onMount } from 'svelte';
	import ProductPrice from '$lib/components/Product/ProductPrice.svelte';
	import { createBreakpointStore } from '$lib/store/breakpoint.store';
	import { collectionSideDrawer } from '$lib/store/collectionSideDrawer.store';
	import { recordAddToCart } from '$lib/posthog';

	export let options: RequiredProduct['options'];
	export let lightVariants: RequiredProduct['variants'] | null;
	export let variant: CompleteVariant;
	export let sdsVariant: SDSVariant;
	export let colormap: RequiredProduct['colormap'];
	export let hasSizeData: boolean;
	export let className: string | undefined;
	export let isSingle: boolean;

	export { className as class };

	$: maxQuantity = $cart.getRealQuantity(
		variant?.sku?.value || '',
		Math.max(sdsVariant?.quantityAvailable ?? 0, variant?.quantityAvailable ?? 0)
	);
	$: parsedColorMap = JSON.parse(colormap?.value ?? '{}');
	$: isBoat = $page.params.branch === 'boat-sport';

	$: selectedOptions = variant.selectedOptions;

	$: generatedOptions =
		selectedOptions
			?.map((option) => {
				return {
					[option.name]: option.value
				};
			})
			.reduce((acc, curr) => ({ ...acc, ...curr }), {}) || {};

	let amount = 1;
	let optionMap = generatedOptions;
	let addedLineIds: string[] = [];
	let showProductAddedToCart = false;

	// This makes the options non-reactive so that we can store them in the session storage
	const setOptions = (data: any) => {
		optionMap = data;
	};
	const getOptions = () => optionMap;

	$: if (generatedOptions) {
		setOptions(generatedOptions);
	}

	$: if (browser) {
		sessionStorage.setItem(`optionsBefore-${$page.params.handle}`, JSON.stringify(getOptions()));
	}

	const switchVariant = (sku: string | null) => {
		if (!sku) {
			if (lightVariants) {
				const firstVariant = mapToNode(lightVariants)[0];
				switchVariant(firstVariant.sku?.value);
				return;
			}

			goto(
				resolveRoute('/[locale]-[country]/[branch=branch]/[slug]/[handle]', {
					...$page.params
				}),
				{
					replaceState: true
				}
			);
			return;
		}
		const urlData: URL = $page.url;
		const url = new URL(urlData.toString());
		url.searchParams.set('variantSKU', sku);
		goto(url.toString(), {
			replaceState: true
		});
	};

	const getLeastChangedVariantOption = () => {
		const optionsBefore = JSON.parse(
			sessionStorage.getItem(`optionsBefore-${$page.params.handle}`) || '{}'
		);
		const changedParam = Object.keys(optionMap).find((key) => {
			return optionsBefore[key] !== optionMap[key];
		});

		// Now we choose the variant that is required to have the changed option and retain the rest on a best effort basis
		const variantWithChangedOption = mapToNode(lightVariants).filter((variant) => {
			return variant.selectedOptions.find((option) => {
				if (option.name === changedParam) {
					return option.value === optionMap[option.name];
				}
				return false;
			});
		});
		console.log('variantWithChangedOption', variantWithChangedOption, changedParam);

		// Now we removed all the options that are not required to be changed and choose the variant which changes the least
		const variantsSortedByLeastChange = variantWithChangedOption
			.map((variant) => {
				const changedOptions = variant.selectedOptions.filter((option) => {
					if (option.name === changedParam) {
						return false;
					}
					return optionsBefore[option.name] !== option.value;
				});
				return {
					variant,
					changedOptions: changedOptions.length
				};
			})
			.sort((a, b) => {
				return a.changedOptions - b.changedOptions;
			});

		return variantsSortedByLeastChange[0]?.variant.sku?.value;
	};

	const handleChange = () => {
		const variantWithCorrectOptions = mapToNode(lightVariants).find((variant) => {
			return variant.selectedOptions.every((option) => {
				return option.value === optionMap[option.name];
			});
		});

		if (variantWithCorrectOptions?.sku?.value) {
			console.info('found variant with correct options', variantWithCorrectOptions.sku.value);
			switchVariant(variantWithCorrectOptions.sku.value);
		} else {
			console.info('no variant with correct options found, fallback to diff check');

			// Try to get any option where the changed value is correct, otherwise fallback to first variant
			const leastChangedVariant = getLeastChangedVariantOption();
			if (leastChangedVariant) {
				switchVariant(leastChangedVariant);
			} else {
				switchVariant(null);
			}
		}
	};

	const isOptionColorDisplayable = (optionValues: string[]) => {
		return (
			optionValues.filter((optionValue) => {
				return !!parsedColorMap[optionValue];
			}).length >
			optionValues.length / 2
		);
	};

	/**
	 * Some [slug] have nested options that are not available with every combination.
	 * By filtering every layer by the options before we can make sure that only valid options are displayed depending on the previous selections.
	 *
	 * @param index the current layer index
	 * @param currentOptionName the option name of the current layer
	 *
	 * @returns the available options for the current layer
	 */
	const getOptionLimitationsOnLayer = (index: number, currentOptionName: string) => {
		if (index === 0) {
			return options[0].values;
		}
		const optionsBefore = options.slice(0, index).map((e) => e.name);
		const availableVariants = mapToNode(lightVariants).filter((variant) => {
			for (const option of optionsBefore) {
				if (variant.selectedOptions.find((e) => e.name === option)?.value !== optionMap[option]) {
					return false;
				}
			}
			return true;
		});
		return deduplicate(
			availableVariants.map((variant) => {
				return variant.selectedOptions.find((e) => e.name === currentOptionName)?.value;
			})
		).filter((e) => !!e) as string[];
	};

	const { t } = getTranslate();

	let buyButtonRef: HTMLButtonElement;
	let buyButtonVisible = true;
	let floatingExpanded = true;
	let scrollY = 0;
	let innerHeight = 0;
	$: scrollProgress = scrollY / innerHeight;
	let totalBrowserHeight = 1;
	$: hasReachedFooter = browser
		? totalBrowserHeight - (scrollY + innerHeight) < ($isMobile ? 700 : 80)
		: false;

	onMount(() => {
		const resizeObserver = new ResizeObserver(() => {
			totalBrowserHeight = document.documentElement.scrollHeight;
		});
		resizeObserver.observe(document.documentElement);

		const observer = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				buyButtonVisible = entry.isIntersecting;
			}
		});
		observer.observe(buyButtonRef);

		return () => {
			observer.disconnect();
			resizeObserver.disconnect();
		};
	});

	const addToCart = async () => {
		recordAddToCart({
			sku: variant?.sku?.value || '',
			source: 'Product Detail Page',
			quantity: amount,
			handle: $page.data.product.handle
		});
		console.log('adding stuff to cart');
		const lineId = await cart.addSDSItem({
			sku: variant.sku?.value || '',
			variantId: variant.id,
			sdsId: sdsVariant.id,
			sdsThreshold: Number(variant.sds_qty?.value || '10') || 10,
			quantity: amount,
			handle: $page.data.product.handle,
			sdsOnHand: sdsVariant.quantityAvailable || 0,
			avantradoOnHand: variant.quantityAvailable || 0
		});

		if (lineId) {
			addedLineIds = [...addedLineIds, lineId];
			showProductAddedToCart = true;
		}

		const gtmData = getGtmPayloadProductAddedToCart({
			productVariantDetails: {
				price: variant.price.amount,
				product_category: variant.product.collections['edges'][0]?.node.title ?? '',
				variant_id: variant.sku?.value ?? ''
			}
		});
		gtmDispatch(gtmData);
	};

	const isMobile = createBreakpointStore();

	const notifyOutOfStock = () => {
		outOfStockDrawerExpanded.set(true);
	};

	const isSizeOption = (name: string) => {
		const optionNames = ['grösse', 'größe', 'länge', 'size', 'length'];
		return optionNames.includes(name);
	};
</script>

<svelte:window bind:scrollY bind:innerHeight />

<form on:change={handleChange} on:submit|preventDefault={() => {}} class={classes(className)}>
	{#if !isSingle}
		{#each options as option, i}
			{@const realOptions = getOptionLimitationsOnLayer(i, option.name)}
			{@const isColorDisplayable = isOptionColorDisplayable(realOptions)}
			{@const isSize = isSizeOption(option.name.toLowerCase())}
			<div class="flex flex-col gap-2.5">
				<label for={option.name} class="text-h-12 font-medium flex justify-between">
					<span>
						{option.name}: <span class="text-p-12 ml-1">{optionMap[option.name]}</span>
					</span>
					<span class="underline text-p-12">
						{#if hasSizeData && !isBoat && isSize}
							<button
								class={classes(
									'underline hover:text-dark-secondary',
									!hasSizeData && 'cursor-default hover:text-current'
								)}
								type="button"
								on:click={() =>
									pushState('', {
										informationButton: { category: 'size' }
									})}
							>
								<T keyName="productDetail.showSizeTable" />
							</button>
						{:else if isBoat}
							<button
								class="normal-case underline"
								type="button"
								on:click={() =>
									pushState('', {
										informationButton: { category: 'multibranche' }
									})}
							>
								{$t('productDetail.options.amount', { amount: option.values.length.toFixed(0) })}
							</button>
						{:else}
							<span class="normal-case underline">
								{$t('productDetail.options.amount', { amount: option.values.length.toFixed(0) })}
							</span>
						{/if}
					</span>
				</label>
				<fieldset name={option.name} id={option.name} class="flex gap-2 flex-wrap">
					{#each realOptions as value}
						{@const isSelected = optionMap[option.name] === value}
						<label
							for={`${option.name}-${value}`}
							id={`${option.name}-${value}-label`}
							class="border-1.5 rounded-lg p-2 cursor-pointer text-p-12 flex items-center gap-2"
							class:border-accent-primary={isSelected}
							class:border-dark-tertiary={!isSelected}
						>
							{#if isColorDisplayable}
								<div
									class="w-5 h-5 rounded-full border border-black grid place-items-center"
									style:background-color={parsedColorMap[value]}
								>
									{#if !parsedColorMap[value]}
										?
									{/if}
								</div>
							{/if}
							<input
								type="radio"
								name={option.name}
								id={`${option.name}-${value}`}
								{value}
								bind:group={optionMap[option.name]}
								class="hidden"
							/>
							{value}
						</label>
					{/each}
				</fieldset>
			</div>
		{/each}
	{/if}
	<!-- <AmountSelection {maxQuantity} initialAmount={1} bind:amount class="py-2" /> -->
	<div class="flex col-span-auto gap-2">
		<ButtonPrimary
			bind:buttonRef={buyButtonRef}
			on:click={async () => {
				await addToCart();
			}}
			disabled={maxQuantity === 0}
			text={maxQuantity === 0 ? $t('product.outOfStock') : $t('product.intoCart')}
			class="w-full"
		/>
		<ProductShareButton
			product={$page.data?.product}
			title={$page.data?.product?.title}
			image={{
				filename:
					parseImageUrls(
						$page.data?.product?.imageUrls?.value ?? $page.data?.product?.variant?.imageUrls?.value
					)?.[0] || ''
			}}
		/>
	</div>
	<div class="col-span-auto">
		{#if maxQuantity === 0}
			<ButtonPrimary
				type="button"
				on:click={() => notifyOutOfStock()}
				text={$t('product.notifyMe')}
				class="w-full"
			/>
		{/if}
	</div>
</form>
<ProductAddedToCartDrawer lineIds={addedLineIds} bind:expanded={showProductAddedToCart} />
<div
	class={classes(
		'fixed right-0 bottom-1/8 transform duration-1000 transition bg-white shadow-xl z-30 flex justify-between items-center px-3 py-4',
		buyButtonVisible ||
			$collectionSideDrawer.isExpanded ||
			showProductAddedToCart ||
			(scrollProgress < 0.8 && $isMobile) ||
			hasReachedFooter
			? 'translate-x-full opacity-0'
			: 'opacity-100',
		floatingExpanded ? 'w-3/4 lg:w-1/2 xl:w-3/7' : ''
	)}
>
	<div class="flex items-center">
		<button
			on:click={() => (floatingExpanded = !floatingExpanded)}
			class="i-sprenger-chevron-right w-6 h-6 mr-4 transition transform duration-500 md:hidden flex-none"
			class:rotate-180={!floatingExpanded}
		></button>
		<div class="flex-col" class:hidden={!floatingExpanded} class:flex={floatingExpanded}>
			<div class="flex text-p-12">
				{#if $page.data?.product?.is_single?.value !== 'true'}
					{#each selectedOptions as { value }, i}
						{value}
						{#if i !== selectedOptions.length - 1}
							/{' '}
						{/if}
					{/each}
				{/if}
			</div>
			<div class="font-semibold text-l flex gap-x-1 flex-wrap">
				<span class="line-clamp-2">{variant.product?.title} - </span>
				<ProductPrice price={variant?.price} />
			</div>
			<p
				on:click={() =>
					window.scrollTo({
						behavior: 'smooth',
						top: 1
					})}
				class="text-accent-primary text-h-12 uppercase mt-1 cursor-pointer"
			>
				{$t('product.showDetails')}
			</p>
		</div>
	</div>
	<div>
		{#if maxQuantity === 0}
			<ButtonPrimary
				type="button"
				on:click={() => notifyOutOfStock()}
				text={$isMobile ? undefined : $t('product.notifyMe')}
				class="w-full"
				iconPosition="left"
			>
				<div slot="icon" class="i-sprenger-bell w-6 h-6 md:hidden"></div>
			</ButtonPrimary>
		{:else}
			<ButtonPrimary
				on:click={async () => {
					await addToCart();
				}}
				disabled={maxQuantity === 0}
				text={$isMobile ? undefined : $t('product.intoCart')}
				class="w-full"
				iconPosition="left"
			>
				<div slot="icon" class="i-sprenger-shopping-cart w-6 h-6 md:hidden"></div>
			</ButtonPrimary>
		{/if}
	</div>
</div>
